// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback } from 'react';

import OfferEditor from 'Containers/Offers/OfferEditor';
import useExchangeRates from 'Hooks/useExchangeRates';
import useCart from 'Hooks/useCart';
import useFetch from 'Hooks/useFetch';
import useOffers from 'Hooks/useOffers';
import useRouter from 'Hooks/useRouter';
import { API_CART, API_OFFERS } from 'Constants';
import download from 'Utils/download';

const getSavedOffer = response => (
  response?.offer
  || response?.data?.offer
  || response?.data
  || response
);

const PrivateOfferEditor = memo(() => {
  const fetcher = useFetch();
  const { refresh: refreshCart } = useCart();
  const { exchangeRates, exchangeRatesLoading } = useExchangeRates();
  const { params, push } = useRouter();
  const {
    offers,
    isHydrated,
    updateOffer,
    addProductToOffer,
    updateOfferProduct,
    removeProductFromOffer,
    deleteOffer,
    moveOfferToSaved
  } = useOffers();
  const routeOfferId = String(params.id || '').toLowerCase();
  const offer = offers.find(item => String(item.id).toLowerCase() === routeOfferId);

  const handleSave = useCallback(payload => fetcher(API_OFFERS, {
    method: 'POST',
    body: JSON.stringify(payload)
  }), [fetcher]);

  const handleExport = useCallback(({ format, payload }) => download(
    `${ API_OFFERS }/export/${ encodeURIComponent(format) }`,
    {
      method: 'POST',
      payload
    }
  ), []);

  const handleAddToCartPreview = useCallback(currentOffer => {
    const items = (currentOffer.items || []).map(item => {
      const quantity = Number(item.quantity || item.minQuantity || 1);
      const stock = Number(item.stockValue ?? item.stock ?? 0);
      const minQuantity = Math.max(1, Number(item.minQuantity || 1));
      let status = null;
      if (quantity % minQuantity !== 0) {
        status = 'PRODUCT_QUANTITY_NOT_A_MULTIPLE';
      } else if (stock <= 0) {
        status = 'PRODUCT_OUT_OF_STOCK';
      } else if (stock < quantity) {
        status = 'PRODUCT_INSUFFICIENT_STOCK';
      }
      return {
        ...item,
        quantity,
        stock,
        minQuantity,
        isAddable: !status,
        status
      };
    });
    const addableCount = items.filter(item => item.isAddable).length;

    return Promise.resolve({
      offerId: currentOffer.id,
      checkedAt: new Date().toISOString(),
      isFilled: addableCount === items.length,
      addableCount,
      skippedCount: items.length - addableCount,
      skippedProductIds: items
        .filter(item => !item.isAddable)
        .map(item => item.productId || item.id),
      items
    });
  }, []);

  const handleAddOfferToCart = useCallback(async preview => {
    const items = (preview?.items || [])
      .filter(item => item.isAddable)
      .map(item => ({
        productId: item.productId || item.id,
        quantity: Number(item.quantity || item.minQuantity || 1),
        minQuantity: Number(item.minQuantity || 1),
        ...(item.supplierId && item.supplierId !== 'default' && {
          supplierId: item.supplierId,
          supplierOfferId: item.supplierOfferId
        })
      }));

    if (!items.length) return null;
    const response = await fetcher(`${ API_CART }?clear_cart=1`, {
      method: 'POST',
      noCancel: true,
      body: JSON.stringify({ items })
    });
    await refreshCart();
    return response;
  }, [fetcher, refreshCart]);

  const handleSaved = useCallback(response => {
    const savedOffer = getSavedOffer(response);
    if (savedOffer?.id) {
      moveOfferToSaved(offer.id, savedOffer.id);
    } else {
      deleteOffer(offer.id);
    }
    push(savedOffer?.id ? `/offers/${ savedOffer.id }/editor` : '/offers');
  }, [
    deleteOffer,
    moveOfferToSaved,
    offer?.id,
    push
  ]);

  return (
    <OfferEditor
      offer={ offer }
      exchangeRates={ exchangeRates }
      isLoading={ !isHydrated || exchangeRatesLoading }
      isSavedOffer={ false }
      updateOffer={ updateOffer }
      addProductToOffer={ addProductToOffer }
      updateOfferProduct={ updateOfferProduct }
      removeProductFromOffer={ removeProductFromOffer }
      exportOffer={ handleExport }
      previewAddToCart={ handleAddToCartPreview }
      addOfferToCart={ handleAddOfferToCart }
      onSave={ handleSave }
      onSaved={ handleSaved }
    />
  );
});

PrivateOfferEditor.displayName = 'PrivateOfferEditorContainer';

export default PrivateOfferEditor;
